/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import ToggleItemWithTooltip from "@/components/ToggleItem/ToggleItemWithTooltip";

import ContentBox from "../../../components/ContentBox/ContentBox";
import {
  EMAIL_NOTIFICATION_FIELDS,
  EmailNotificationsPayload,
  SettingsPayloadDto
} from "../types";

interface EmailNotificationsProps {
  emailNotificationSettings: EmailNotificationsPayload | null;
  isDisabled: boolean;
  setSettingsPayload: Dispatch<SetStateAction<SettingsPayloadDto>>;
  setIsTriggered: Dispatch<SetStateAction<boolean>>;
}

const EmailNotifications = ({
  isDisabled,
  setIsTriggered,
  setSettingsPayload,
  emailNotificationSettings
}: EmailNotificationsProps) => {
  const { t } = useTranslation();

  const handleToggle = (fieldName: EMAIL_NOTIFICATION_FIELDS) => {
    setIsTriggered(true);

    setSettingsPayload(prev => {
      const emailNotificationSettings = {
        creditsAddedAlert:
          prev.emailNotificationSettings?.creditsAddedAlert ?? false,
        lowCreditsAlert:
          prev.emailNotificationSettings?.lowCreditsAlert ?? false
      };

      return {
        ...prev,
        emailNotificationSettings: {
          ...emailNotificationSettings,
          [fieldName]: !emailNotificationSettings[fieldName]
        }
      };
    });
  };

  return (
    <div css={css({ width: "100%", marginTop: "32px" })}>
      <ContentBox
        title={t("Settings##email notifications##title")}
        description={t("Settings##email notifications##tooltip")}
        noFlex
      >
        <ToggleItemWithTooltip
          data-testid="credits-purchased-toggle"
          showSeparator
          isDisabled={isDisabled}
          title={t("Settings##email notifications##credits purchased")}
          onToggle={() =>
            handleToggle(EMAIL_NOTIFICATION_FIELDS.CreditsAddedAlert)
          }
          isChecked={Boolean(emailNotificationSettings?.creditsAddedAlert)}
        />

        <ToggleItemWithTooltip
          data-testid="low-credits-alerts-toggle"
          isDisabled={isDisabled}
          title={t("Settings##email notifications##low credits alerts")}
          onToggle={() =>
            handleToggle(EMAIL_NOTIFICATION_FIELDS.LowCreditsAlert)
          }
          isChecked={Boolean(emailNotificationSettings?.lowCreditsAlert)}
          customContainerStyle={{
            paddingBottom: 0
          }}
        />
      </ContentBox>
    </div>
  );
};

export default EmailNotifications;
